import React, { useState, useContext } from "react";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import HeaderBar from "../components/HeaderBar";
import ProjectPostOrderLayoutWrapper from "../components/ProjectPostOrderLayoutWrapper";
import ProjectPerOrderLayoutWrapper from "../components/ProjectPreOrderLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { 
  FaSearch, 
  FaFilter, 
  FaFileAlt, 
  FaChevronUp, 
  FaChevronDown,
  FaDownload,
  FaPrint,
  FaEye,
  FaCheck,
  FaCalendarAlt,
  FaCheckCircle
} from "react-icons/fa";

const EstimationApprovedReport = ({type}) => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const LayoutWrapper =
    type === "preorder"
      ? ProjectPerOrderLayoutWrapper
      : ProjectPostOrderLayoutWrapper;

  // State to toggle filter form visibility
  const [showFilters, setShowFilters] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Filter state
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    projectName: "",
    estimationCode: "",
    minAmount: "",
    maxAmount: "",
    approvalStatus: "approved"
  });

  // Sample data for the estimation approved report
  const estimationData = [
    { sno: 1, estimationCode: "EST/39", projectName: "A PROJECTS WORK", estimationCost: "380700.00" },
    { sno: 2, estimationCode: "EST/2047", projectName: "Building Project 07-02-2022", estimationCost: "901343.95" },
    { sno: 3, estimationCode: "EST/2066", projectName: "Building Work(14/05/2022)", estimationCost: "46365.00" },
    { sno: 4, estimationCode: "EST/2", projectName: "CBE to MTP Batch Work", estimationCost: "61800.00" },
    { sno: 5, estimationCode: "EST/3", projectName: "CBE to MTP Batch Work Part 2", estimationCost: "5773.50" },
    { sno: 6, estimationCode: "EST/2053", projectName: "construct (1/4/2022)", estimationCost: "78000.00" },
    { sno: 7, estimationCode: "EST/43", projectName: "construction of tumtikawn road work", estimationCost: "78269000.00" },
    { sno: 8, estimationCode: "EST/47", projectName: "D HIGHWAY CONSTRUCTION", estimationCost: "1503000.00" },
    { sno: 9, estimationCode: "EST/2061", projectName: "DLF CONSTRUCTION", estimationCost: "1250375.00" },
    { sno: 10, estimationCode: "EST/2051", projectName: "GRAND BRIDGE CONSTRUCTION", estimationCost: "825000.00" },
    { sno: 11, estimationCode: "EST/2068", projectName: "Halwa Machine Making", estimationCost: "96900.00" },
    { sno: 12, estimationCode: "EST/2050", projectName: "HOUSING CONSTRUCTION", estimationCost: "290000.00" },
    { sno: 13, estimationCode: "EST/2062", projectName: "JRJ ROAD CONSTRUCT", estimationCost: "405500.00" },
    { sno: 14, estimationCode: "EST/2056", projectName: "JSW CONSTRUCTION", estimationCost: "193750.00" },
    { sno: 15, estimationCode: "EST/46", projectName: "KOVAI TO HOPES", estimationCost: "6596175.00" },
    { sno: 16, estimationCode: "EST/2067", projectName: "Machine Making", estimationCost: "56600.00" },
    { sno: 17, estimationCode: "EST/2054", projectName: "MODEL CONSTRUCTION", estimationCost: "621500.00" },
    { sno: 18, estimationCode: "EST/2071", projectName: "MYSORE PAK MACHINE", estimationCost: "327200.00" },
    { sno: 19, estimationCode: "EST/2049", projectName: "Project Work 12-02-2022", estimationCost: "23297037.49" },
    { sno: 20, estimationCode: "EST/2065", projectName: "ROAD INFRA", estimationCost: "88500.00" },
    { sno: 21, estimationCode: "EST/1046", projectName: "ROAD LAYER", estimationCost: "17500.00" },
    { sno: 22, estimationCode: "EST/41", projectName: "road test", estimationCost: "154378.00" },
    { sno: 23, estimationCode: "EST/2052", projectName: "ROADING CONSTRUCT", estimationCost: "495000.00" },
    { sno: 24, estimationCode: "EST/2072", projectName: "Site preparation", estimationCost: "2320000.00" },
    { sno: 25, estimationCode: "EST/1045", projectName: "test", estimationCost: "174455.00" }
  ];

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearch = () => {
    toast.success("Search filters applied!");
  };

  const handleExport = () => {
    toast.success("Report exported successfully!");
  };

  const handlePrint = () => {
    toast.success("Report sent to printer!");
  };

  const handleViewDetails = (estimationCode) => {
    toast.info(`Viewing details for ${estimationCode}`);
  };

  // Filter data based on search term
  const filteredData = estimationData.filter(item =>
    item.projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.estimationCode.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate total amount
  const totalAmount = filteredData.reduce((sum, item) => sum + parseFloat(item.estimationCost), 0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <LayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ESTIMATION APPROVED REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : "bg-white text-black"
        }`}>
          {/* Header Section */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <FaCheckCircle className={`text-2xl ${isDark ? "text-green-400" : "text-green-600"}`} />
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Estimation Approved Report
              </h1>
            </div>

            <div className="flex items-center gap-3">
              {/* Search Bar */}
              <div className="relative">
                <FaSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                  isDark ? "text-gray-400" : "text-gray-500"
                }`} />
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`pl-10 pr-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    isDark 
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" 
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }`}
                />
              </div>

              {/* Action Buttons */}
              <button
                onClick={handleExport}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-green-600 hover:bg-green-700 text-white"
                    : "bg-green-500 hover:bg-green-600 text-white"
                }`}
              >
                <FaDownload />
                Export
              </button>

              <button
                onClick={handlePrint}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-purple-600 hover:bg-purple-700 text-white"
                    : "bg-purple-500 hover:bg-purple-600 text-white"
                }`}
              >
                <FaPrint />
                Print
              </button>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* Filter Section */}
          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${
              isDark ? "bg-gray-800/50" : "bg-green-50"
            }`}>
              <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                isDark ? "text-white" : "text-green-900"
              }`}>
                <FaFilter />
                Filter Options
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    From Date
                  </label>
                  <input
                    type="date"
                    value={filters.fromDate}
                    onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all ${
                      isDark 
                        ? "bg-gray-700 border-gray-600 text-white" 
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    To Date
                  </label>
                  <input
                    type="date"
                    value={filters.toDate}
                    onChange={(e) => handleFilterChange('toDate', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all ${
                      isDark 
                        ? "bg-gray-700 border-gray-600 text-white" 
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    Project Name
                  </label>
                  <input
                    type="text"
                    placeholder="Enter project name"
                    value={filters.projectName}
                    onChange={(e) => handleFilterChange('projectName', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all ${
                      isDark 
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" 
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    Estimation Code
                  </label>
                  <input
                    type="text"
                    placeholder="EST/..."
                    value={filters.estimationCode}
                    onChange={(e) => handleFilterChange('estimationCode', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all ${
                      isDark 
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" 
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    Min Amount
                  </label>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={filters.minAmount}
                    onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all ${
                      isDark 
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" 
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    Max Amount
                  </label>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={filters.maxAmount}
                    onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all ${
                      isDark 
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" 
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={handleSearch}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    isDark ? `${buttonClass} ${hoverClass} text-white` : `${buttonClass} ${hoverClass} text-white`
                  }`}
                >
                  <FaSearch />
                  Apply Filters
                </button>
              </div>
            </div>
          )}

          {/* Summary Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className={`p-4 rounded-lg border ${
              isDark 
                ? "bg-green-900/20 border-green-700" 
                : "bg-green-50 border-green-200"
            }`}>
              <div className="text-center">
                <h3 className={`text-lg font-semibold ${
                  isDark ? "text-green-300" : "text-green-900"
                }`}>
                  Total Approved
                </h3>
                <p className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-green-600"
                }`}>
                  {filteredData.length}
                </p>
              </div>
            </div>

            <div className={`p-4 rounded-lg border ${
              isDark 
                ? "bg-blue-900/20 border-blue-700" 
                : "bg-blue-50 border-blue-200"
            }`}>
              <div className="text-center">
                <h3 className={`text-lg font-semibold ${
                  isDark ? "text-blue-300" : "text-blue-900"
                }`}>
                  Total Value
                </h3>
                <p className={`text-xl font-bold ${
                  isDark ? "text-white" : "text-blue-600"
                }`}>
                  ₹{formatCurrency(totalAmount)}
                </p>
              </div>
            </div>

            <div className={`p-4 rounded-lg border ${
              isDark 
                ? "bg-orange-900/20 border-orange-700" 
                : "bg-orange-50 border-orange-200"
            }`}>
              <div className="text-center">
                <h3 className={`text-lg font-semibold ${
                  isDark ? "text-orange-300" : "text-orange-900"
                }`}>
                  Avg Value
                </h3>
                <p className={`text-xl font-bold ${
                  isDark ? "text-white" : "text-orange-600"
                }`}>
                  ₹{formatCurrency(totalAmount / filteredData.length || 0)}
                </p>
              </div>
            </div>

            <div className={`p-4 rounded-lg border ${
              isDark 
                ? "bg-purple-900/20 border-purple-700" 
                : "bg-purple-50 border-purple-200"
            }`}>
              <div className="text-center">
                <h3 className={`text-lg font-semibold ${
                  isDark ? "text-purple-300" : "text-purple-900"
                }`}>
                  Status
                </h3>
                <p className={`text-xl font-bold flex items-center justify-center gap-2 ${
                  isDark ? "text-white" : "text-purple-600"
                }`}>
                  <FaCheck />
                  Approved
                </p>
              </div>
            </div>
          </div>

          {/* Data Table */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${
            isDark ? "border-gray-700" : "border-gray-200"
          }`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-6 py-4 text-center font-semibold border-r border-white/20">
                      S.no
                    </th>
                    <th className="px-6 py-4 text-center font-semibold border-r border-white/20">
                      Estimation Code
                    </th>
                    <th className="px-6 py-4 text-center font-semibold border-r border-white/20">
                      Project Name
                    </th>
                    <th className="px-6 py-4 text-center font-semibold border-r border-white/20">
                      Estimation Cost
                    </th>
                    <th className="px-6 py-4 text-center font-semibold">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${
                  isDark ? "divide-gray-700" : "divide-gray-200"
                }`}>
                  {filteredData.map((item) => (
                    <tr key={item.sno} className={`transition-colors duration-200 ${
                      isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"
                    }`}>
                      <td className={`px-6 py-4 text-center border-r ${
                        isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"
                      }`}>
                        {item.sno}
                      </td>
                      <td className={`px-6 py-4 text-center border-r font-medium ${
                        isDark ? "border-gray-700 text-blue-300" : "border-gray-200 text-blue-600"
                      }`}>
                        <div className="flex items-center justify-center gap-2">
                          <FaCheckCircle className="text-green-500 text-sm" />
                          {item.estimationCode}
                        </div>
                      </td>
                      <td className={`px-6 py-4 text-left border-r ${
                        isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"
                      }`}>
                        <div className="max-w-xs truncate" title={item.projectName}>
                          {item.projectName}
                        </div>
                      </td>
                      <td className={`px-6 py-4 text-right border-r font-semibold ${
                        isDark ? "border-gray-700 text-green-300" : "border-gray-200 text-green-600"
                      }`}>
                        ₹{formatCurrency(parseFloat(item.estimationCost))}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => handleViewDetails(item.estimationCode)}
                          className={`px-3 py-1 rounded-lg text-xs font-medium transition-all duration-200 hover:scale-105 ${
                            isDark 
                              ? "bg-green-600 hover:bg-green-700 text-white" 
                              : "bg-green-100 hover:bg-green-200 text-green-700"
                          }`}
                        >
                          <FaEye className="inline mr-1" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className={`${buttonClass} text-white`}>
                  <tr>
                    <td colSpan="3" className="px-6 py-4 text-center font-bold border-r border-white/20">
                      TOTAL APPROVED
                    </td>
                    <td className="px-6 py-4 text-right font-bold border-r border-white/20">
                      ₹{formatCurrency(totalAmount)}
                    </td>
                    <td className="px-6 py-4"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Results Info */}
          <div className="mt-4 flex justify-between items-center">
            <div className={`text-sm flex items-center gap-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              <FaCheckCircle className="text-green-500" />
              Showing {filteredData.length} approved estimations
            </div>
            <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              Report generated: {new Date().toLocaleString()}
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default EstimationApprovedReport;